import { ChevronLeftIcon, ChevronRight, Maximize2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { Button } from '@/components/button'
import { DialogContent, DialogTrigger } from '@/components/dialog'
import { Expander } from '@/components/image-expander'
import { ScrollArea, ScrollBar } from '@/components/scroll-area'
import { Skeleton } from '@/components/skeleton'
import { FormatLocaleDate } from '@/utils/format-locale-date'

import { InitialData } from './monitoring-tab'

export interface ImageOptions {
  highRes?: string
  lowRes?: string
}
interface MapCardProps {
  mapData: {
    title: string
    img?: ImageOptions
    date?: string
    data?: InitialData
    isWithCarousel?: boolean
  }
  isLoading?: boolean
}

export function MapCard({
  mapData: { date, img, title, data, isWithCarousel },
  isLoading,
}: MapCardProps) {
  const [featuredIndex, setFeaturedIndex] = useState<number | null>(null)

  const activeThumbRef = useRef<HTMLDivElement>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    const attemptScroll = (attempt = 0) => {
      if (activeThumbRef.current && attempt < 3) {
        const element = activeThumbRef.current

        requestAnimationFrame(() => {
          element?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          })
        })

        if (attempt < 2) {
          scrollTimeoutRef.current = setTimeout(
            () => attemptScroll(attempt + 1),
            150,
          )
        }
      }
    }

    if (featuredIndex !== null) {
      scrollTimeoutRef.current = setTimeout(() => attemptScroll(), 300)
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [featuredIndex])

  if (isLoading) {
    return <Skeleton className="aspect-square w-full" />
  }

  const hasImage = img?.highRes || img?.lowRes

  const entriesArray = data?.imagens
    ? Object.entries(data.imagens).filter(
        ([, image]) => image?.highRes || image?.lowRes,
      )
    : []

  const currentIndex = entriesArray.findIndex(
    ([, image]) =>
      image?.highRes === img?.highRes || image?.lowRes === img?.lowRes,
  )

  const cardTitle = title + (date ? ` | Data: ${FormatLocaleDate(date)}` : '')

  const featuredCard =
    featuredIndex !== null ? entriesArray[featuredIndex]?.[1] : undefined
  const featuredImageSrc = featuredCard?.highRes || featuredCard?.lowRes || ''

  function handleFeaturedPhoto(index: number) {
    setFeaturedIndex(index)
  }

  function handleClickedPreviousOrNextPhoto(isPrevious: boolean) {
    setFeaturedIndex((prevIndex) => {
      if (prevIndex === null) return prevIndex

      const nextIndex = isPrevious ? prevIndex - 1 : prevIndex + 1

      if (nextIndex < 0 || nextIndex >= entriesArray.length) {
        return prevIndex
      }

      return nextIndex
    })
  }

  function isDisabledPreviousButton(): boolean {
    return featuredIndex === null || featuredIndex <= 0
  }

  function isDisabledNextButton(): boolean {
    return featuredIndex === null || featuredIndex >= entriesArray.length - 1
  }

  const fallbackOnError =
    (image?: ImageOptions) =>
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (event.currentTarget.src === image?.highRes) {
        event.currentTarget.onerror = null
        event.currentTarget.src = ''
        return
      }
      if (image?.highRes) {
        event.currentTarget.onerror = null
        event.currentTarget.src = image.highRes
        return
      }
      event.currentTarget.onerror = null
      event.currentTarget.src = ''
    }

  if (!hasImage) {
    return (
      <div className="flex size-80 flex-col">
        <div>
          <p className="text-monochromatic-400 flex aspect-square w-full items-center justify-center select-none">
            Imagem indisponível
          </p>
        </div>

        <p className="text-monochromatic-400 mt-4 text-xs font-bold">
          {cardTitle}
        </p>
      </div>
    )
  }

  return (
    <div>
      <Expander>
        <a
          href={title}
          onClick={(e) => {
            e.preventDefault()
            const initIdx = currentIndex >= 0 ? currentIndex : 0
            handleFeaturedPhoto(initIdx)
          }}
        >
          <DialogTrigger className="group relative w-full">
            <img
              src={img?.lowRes || img?.highRes || ''}
              loading="lazy"
              decoding="async"
              alt={title}
              onError={fallbackOnError(img)}
              className="hover:border-bios-primary aspect-square w-full rounded-sm object-cover select-none hover:border-2"
            />

            <Maximize2 className="pointer-events-none absolute top-1/2 left-1/2 size-10 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-gray-400/40 p-1 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
          </DialogTrigger>
        </a>

        <DialogContent className="h-auto w-[90vw] lg:h-3/4 lg:min-h-[90vh]">
          <div
            className={twMerge(
              'relative h-[87%] w-full items-center gap-4',
              isWithCarousel && data && 'h-4/5 2xl:h-[87%]',
            )}
          >
            {(() => {
              const activeImage = isWithCarousel
                ? featuredImageSrc
                : img?.highRes || img?.lowRes || ''

              if (!activeImage) {
                return (
                  <div className="mx-auto mt-6 flex h-full items-center justify-center text-center select-none lg:mt-0">
                    Imagem indisponível
                  </div>
                )
              }

              return (
                <img
                  key={
                    isWithCarousel && featuredIndex !== null
                      ? entriesArray[featuredIndex]?.[0]
                      : title
                  }
                  className="animate-slideIn mx-auto mt-6 aspect-auto h-full max-w-[90%] object-contain transition-all duration-200 select-none lg:mt-0"
                  src={activeImage}
                  loading="eager"
                  decoding="async"
                  onError={fallbackOnError(isWithCarousel ? featuredCard : img)}
                  onClick={() => {
                    if (isWithCarousel && featuredIndex !== null) {
                      handleFeaturedPhoto(featuredIndex)
                    }
                  }}
                  alt="Imagens da propriedade"
                />
              )
            })()}

            {isWithCarousel && data && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleClickedPreviousOrNextPhoto(true)}
                  disabled={isDisabledPreviousButton()}
                  className="bg-monochromatic-200 text-bios-primary disabled:bg-monochromatic-200 disabled:text-monochromatic-300 absolute top-1/2 left-0 size-8! -translate-y-1/2 transform rounded-sm border-none p-0 shadow-md"
                >
                  <ChevronLeftIcon className="text-bios-primary size-6" />
                </Button>

                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => handleClickedPreviousOrNextPhoto(false)}
                  disabled={isDisabledNextButton()}
                  className="bg-monochromatic-200 text-bios-primary disabled:bg-monochromatic-200 disabled:text-monochromatic-300 absolute top-1/2 right-0 size-8! -translate-y-1/2 transform rounded-sm border-none p-0 shadow-md"
                >
                  <ChevronRight className="text-bios-primary size-6" />
                </Button>
              </>
            )}
          </div>

          {isWithCarousel && data && (
            <div className="w-full">
              <ScrollArea className="mx-auto w-[90%] lg:w-1/2">
                <div className="relative mb-3 flex items-center justify-center gap-3">
                  {entriesArray.map(([key, image], thumbIndex) => (
                    <div
                      key={key}
                      ref={thumbIndex === featuredIndex ? activeThumbRef : null}
                    >
                      <section className="group relative h-full w-28">
                        <img
                          className={twMerge(
                            'hover:border-bios-primary h-20 w-full cursor-pointer rounded-2xl object-cover transition-all select-none hover:border-2',
                            thumbIndex === featuredIndex
                              ? 'border-bios-primary border-2'
                              : 'border-transparent',
                          )}
                          src={image?.lowRes || image?.highRes || ''}
                          loading="lazy"
                          decoding="async"
                          onError={fallbackOnError(image)}
                          alt="Imagens da propriedade"
                          onClick={() => handleFeaturedPhoto(thumbIndex)}
                        />
                        <Maximize2 className="pointer-events-none absolute top-1/2 left-1/2 size-8 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-gray-400/40 p-1 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                      </section>
                    </div>
                  ))}
                </div>

                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Expander>

      <p className="text-monochromatic-400 mt-4 text-xs font-bold">
        {cardTitle}
      </p>
    </div>
  )
}
